import { AnimatePresence, motion } from 'framer-motion';
import type { Translate } from 'next-translate';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Portal } from '@/components/elements/Portal';
import { Button } from '@/components/UI/Button';
import { useKeyPress } from '@/hooks';
import { AmplitudeService } from '@/services';
import { AMPLITUDE_EVENTS } from '@/constants';
import CloseIcon from '@/public/icons/cross.svg';
import styles from './EmailPopup.module.scss';

interface Props {
	isShown: boolean;
	onClose: () => void;
	onSubmit: (email: string) => void;
	isSubmitting?: boolean;
	errorMessage?: string;
	onClearError?: () => void;
	/** Submit label; defaults to the download wording used by the funnels */
	submitText?: string;
	/** Icon shown next to the terms line; the line goes left-aligned with it */
	termsIcon?: React.ReactNode;
	t: Translate;
	tCommon: Translate;
}

interface FormValues {
	email: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_BREAKPOINT = 1023;

const overlayMotion = {
	initial: { opacity: 0 },
	animate: { opacity: 1 },
	exit: { opacity: 0 },
	transition: { duration: 0.2 },
};

const getDialogMotion = (isMobile: boolean) =>
	isMobile
		? {
				initial: { y: '100%' },
				animate: { y: 0 },
				exit: { y: '100%' },
				transition: { type: 'tween' as const, duration: 0.3, ease: 'easeOut' },
		  }
		: {
				initial: { opacity: 0, scale: 0.96 },
				animate: { opacity: 1, scale: 1 },
				exit: { opacity: 0, scale: 0.96 },
				transition: { duration: 0.2, ease: 'easeOut' },
		  };

export const EmailPopup: React.FC<Props> = ({
	isShown,
	onClose,
	onSubmit,
	isSubmitting = false,
	errorMessage,
	onClearError,
	submitText,
	termsIcon,
	t,
	tCommon,
}) => {
	const [isMobile, setIsMobile] = React.useState(false);

	React.useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}
		const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
		const update = () => setIsMobile(mq.matches);
		update();
		mq.addEventListener('change', update);
		return () => mq.removeEventListener('change', update);
	}, []);

	React.useEffect(() => {
		if (typeof document === 'undefined') return;
		document.body.style.overflow = isShown ? 'hidden' : '';
		return () => {
			document.body.style.overflow = '';
		};
	}, [isShown]);

	useKeyPress('Escape', onClose, isShown);

	React.useEffect(() => {
		if (isShown) {
			AmplitudeService.logEvent(AMPLITUDE_EVENTS.EMAIL_POPUP_SHOWN);
		}
	}, [isShown]);

	const {
		register,
		handleSubmit,
		reset,
		watch,
		formState: { isValid, errors },
	} = useForm<FormValues>({ mode: 'onChange', defaultValues: { email: '' } });

	const emailValue = watch('email');
	const showPatternError =
		Boolean(emailValue) && errors.email?.type === 'pattern';

	React.useEffect(() => {
		if (!isShown) reset();
	}, [isShown, reset]);

	const submit = ({ email }: FormValues) => {
		onSubmit(email);
	};

	return (
		<Portal>
			<AnimatePresence>
				{isShown && (
					<motion.div
						role='dialog'
						aria-modal='true'
						className={styles['emailPopup']}
						initial={{ opacity: 1 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 1 }}
					>
						<motion.button
							type='button'
							aria-label='Close'
							className={styles['emailPopup__overlay']}
							onClick={onClose}
							{...overlayMotion}
						/>
						<motion.div
							className={styles['emailPopup__dialog']}
							{...getDialogMotion(isMobile)}
						>
							<button
								type='button'
								aria-label='Close'
								className={styles['emailPopup__close']}
								onClick={onClose}
							>
								<CloseIcon className={styles['emailPopup__close-icon']} />
							</button>
							<div className={styles['emailPopup__body']}>
								<h2 className={styles['emailPopup__title']}>
									{t('emailPopup.title')}
								</h2>
								<form onSubmit={handleSubmit(submit)} noValidate>
									<input
										type='email'
										inputMode='email'
										autoComplete='email'
										placeholder={t('emailPopup.placeholder')}
										className={styles['emailPopup__input']}
										{...register('email', {
											required: true,
											pattern: EMAIL_PATTERN,
											onChange: () => {
												if (errorMessage) onClearError?.();
											},
										})}
									/>
									{(errorMessage || showPatternError) && (
										<p className={styles['emailPopup__error']}>
											{errorMessage || t('emailPopup.invalidEmail')}
										</p>
									)}
									<Button
										as='button'
										type='submit'
										preset='primary'
										text={submitText ?? tCommon('download')}
										size='medium'
										disabled={!isValid || isSubmitting}
										loading={isSubmitting}
										classes={{ button: styles['emailPopup__submit'] }}
									/>
								</form>
								{termsIcon ? (
									<div className={styles['emailPopup__terms-block']}>
										<span className={styles['emailPopup__terms-icon']}>
											{termsIcon}
										</span>
										<p
											className={`${styles['emailPopup__terms']} ${styles['emailPopup__terms_with-icon']}`}
											dangerouslySetInnerHTML={{
												__html: t('emailPopup.terms'),
											}}
										/>
									</div>
								) : (
									<p
										className={styles['emailPopup__terms']}
										dangerouslySetInnerHTML={{ __html: t('emailPopup.terms') }}
									/>
								)}
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</Portal>
	);
};
